import { useCallback, useEffect, useMemo, useState } from 'react'
import { connectedBy, productOf } from '../lib/metaConnections.js'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import {
  getMetaConnections, getMetaPagesAvailable, getMetaPages, linkMetaPages,
  replaceMetaConnectionToken,
} from '../db/api.js'

const CLOSE_MS = 200

// Permissões que a conexão precisa para receber leads de formulário.
const LEAD_SCOPES = ['pages_show_list', 'pages_read_engagement', 'pages_manage_metadata', 'leads_retrieval']

const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--sec)', marginBottom: 6, letterSpacing: '.05em' }
const ROW = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--soft)', fontSize: 14 }

/**
 * Modal "Lead Ads" da conexão Meta Ads: vincula Páginas do Facebook a um cliente
 * para que os leads dos formulários entrem no CRM com a origem do anúncio.
 *
 * As Páginas disponíveis vêm da conexão Meta Ads da organização
 * (/api/meta-ads/pages/available, sem token). O vínculo assina o evento
 * `leadgen` no webhook; o token de cada Página fica só no backend.
 *
 * Props:
 *  - open    : controla montagem.
 *  - onClose : chamado depois da animação de saída.
 */
// Páginas e Lead Ads vêm da conexão do Facebook (#320); a de anúncios e a do Instagram ficam de fora.
const isFacebookConnection = (c) => c.is_active && productOf(c) === 'facebook'

export default function MetaLeadAdsModal({ open, onClose }) {
  const { availableClients, accessMode } = useApp()
  const clients = useMemo(() => availableClients || [], [availableClients])
  const canManage = accessMode !== 'client_only'

  const [mounted, setMounted] = useState(false)
  const [closing, setClosing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [connections, setConnections] = useState([])
  const [connectionId, setConnectionId] = useState('')
  const [available, setAvailable] = useState([])
  const [linked, setLinked] = useState([])
  const [selected, setSelected] = useState([])
  const [clientId, setClientId] = useState('')
  const [tokenDraft, setTokenDraft] = useState('')
  const [savingToken, setSavingToken] = useState(false)

  const connection = connections.find((c) => c.id === connectionId)
  const grantedScopes = (connection?.scope || '').split(',')
  const missingScopes = LEAD_SCOPES.filter((s) => !grantedScopes.includes(s))

  const clientName = (id) => clients.find((c) => c.id === id)?.name || 'Cliente'
  const linkedIds = new Set(linked.map((p) => p.page_id))
  const pending = available.filter((p) => !linkedIds.has(p.page_id))

  const startClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
  }, [onClose])

  const loadPages = useCallback(async (connId) => {
    setError('')
    try {
      const [pages, current] = await Promise.all([
        connId ? getMetaPagesAvailable(connId) : Promise.resolve([]),
        getMetaPages(),
      ])
      setAvailable(Array.isArray(pages) ? pages : [])
      setLinked(Array.isArray(current) ? current : [])
    } catch (e) {
      setError(e.message || 'Não foi possível carregar as Páginas.')
    }
  }, [])

  // A cada abertura: reseta o estado e recarrega a conexão e as Páginas.
  useEffect(() => {
    if (!open) return undefined
    let alive = true
    setMounted(true)
    setClosing(false)
    setError('')
    setSuccess('')
    setSelected([])
    setTokenDraft('')
    setLoading(true)
    ;(async () => {
      try {
        const data = await getMetaConnections()
        const active = (Array.isArray(data) ? data : []).filter(isFacebookConnection)
        if (!alive) return
        setConnections(active)
        const first = active[0]?.id || ''
        setConnectionId(first)
        await loadPages(first)
      } catch (e) {
        if (alive) setError(e.message || 'Não foi possível carregar a conexão Meta.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [open, loadPages])

  useEffect(() => {
    if (!clientId && clients[0]?.id) setClientId(clients[0].id)
  }, [clients, clientId])

  // Esc fecha.
  useEffect(() => {
    if (!mounted) return undefined
    const onKey = (e) => e.key === 'Escape' && startClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  const toggle = (pageId) =>
    setSelected((prev) => (prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]))

  async function changeConnection(id) {
    setConnectionId(id)
    setSelected([])
    await loadPages(id)
  }

  async function handleSaveToken(event) {
    event.preventDefault()
    const token = tokenDraft.trim()
    if (!token || !connectionId) return
    setSavingToken(true)
    setError('')
    setSuccess('')
    try {
      const result = await replaceMetaConnectionToken(connectionId, token)
      setTokenDraft('')
      setSuccess(`Token atualizado. ${result.pages_found} Página(s) encontrada(s).`)
      const data = await getMetaConnections()
      setConnections((Array.isArray(data) ? data : []).filter(isFacebookConnection))
      await loadPages(connectionId)
    } catch (e) {
      setError(e.message || 'Não foi possível atualizar o token.')
    } finally {
      setSavingToken(false)
    }
  }

  async function handleLink() {
    if (!clientId || selected.length === 0) return
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      const result = await linkMetaPages({ connectionId, clientId, pageIds: selected })
      const failed = result.filter((p) => !p.subscribed)
      if (failed.length) {
        setError(`${failed.length} Página(s) vinculada(s), mas sem receber leads: ${failed[0].error}`)
      } else {
        setSuccess(`${result.length} Página(s) vinculada(s) a ${clientName(clientId)}.`)
      }
      setSelected([])
      await loadPages(connectionId)
    } catch (e) {
      setError(e.message || 'Não foi possível vincular as Páginas.')
    } finally {
      setBusy(false)
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay${closing ? ' is-closing' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && startClose()}
    >
      <div className="sup-modal" role="dialog" aria-modal="true" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="sup-close" onClick={startClose} aria-label="Fechar">
          <Icon n="x" s={16} />
        </button>

        <div className="sup-head">
          <div className="sup-title">Lead Ads (Meta)</div>
          <div className="sup-subtitle">
            Leads dos formulários da Página entram no CRM com a campanha, o conjunto e o anúncio de origem.
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div className="auth-error" role="alert">{error}</div>}
          {success && (
            <div
              className="auth-error"
              style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}
              role="status"
            >
              {success}
            </div>
          )}

          {loading ? (
            <div className="empty"><div className="empty-desc">Carregando Páginas…</div></div>
          ) : connections.length === 0 ? (
            <div className="empty">
              <div className="empty-desc">
                Nenhum Facebook conectado. Conecte o Facebook em Conexões para escolher as Páginas.
              </div>
            </div>
          ) : (
            <>
              {connections.length > 1 && (
                <label style={{ fontSize: 13, color: 'var(--sec)' }}>
                  Conta Meta
                  <select
                    className="input"
                    style={{ display: 'block', width: '100%', marginTop: 4 }}
                    value={connectionId}
                    onChange={(e) => changeConnection(e.target.value)}
                    disabled={busy}
                  >
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{connectedBy(c)}</option>
                    ))}
                  </select>
                </label>
              )}

              {canManage && missingScopes.length > 0 && (
                <section aria-label="Token da conexão">
                  <div style={LABEL}>TOKEN DA CONEXÃO</div>
                  <p className="sup-subtitle" style={{ marginTop: 0 }}>
                    Esta conexão não tem as permissões: {missingScopes.join(', ')}. No Business Manager,
                    gere um token do usuário de sistema com essas permissões e cole abaixo. O token é
                    validado na Meta e guardado cifrado.
                  </p>
                  <form onSubmit={handleSaveToken} style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      type="password"
                      autoComplete="off"
                      aria-label="Token do usuário de sistema"
                      placeholder="Cole o token aqui"
                      style={{ flex: 1 }}
                      value={tokenDraft}
                      onChange={(e) => setTokenDraft(e.target.value)}
                      disabled={savingToken}
                    />
                    <button className="btn btn-black" type="submit" disabled={savingToken || !tokenDraft.trim()}>
                      {savingToken ? 'Validando…' : 'Atualizar token'}
                    </button>
                  </form>
                </section>
              )}

              <section>
                <div style={LABEL}>VINCULADAS</div>
                {linked.length === 0 ? (
                  <p className="sup-subtitle">Nenhuma Página vinculada ainda.</p>
                ) : (
                  linked.map((p) => {
                    const ok = p.subscribed_at && !p.last_error
                    return (
                      <div key={p.id} style={ROW}>
                        <span style={{ fontWeight: 600 }}>{p.page_name || p.page_id}</span>
                        <span style={{ color: ok ? '#16a34a' : '#d97706', textAlign: 'right' }}>
                          {clientName(p.client_id)} · {ok ? 'Recebendo leads' : 'Reautorizar'}
                        </span>
                      </div>
                    )
                  })
                )}
              </section>

              {canManage && (
                <section>
                  <div style={LABEL}>DISPONÍVEIS</div>
                  {pending.length === 0 ? (
                    <p className="sup-subtitle">
                      {available.length === 0
                        ? 'Nenhuma Página encontrada. Reconecte o Meta Ads e autorize o acesso às Páginas.'
                        : 'Todas as Páginas desta conta já estão vinculadas.'}
                    </p>
                  ) : (
                    <>
                      {pending.map((p) => (
                        <label
                          key={p.page_id}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14, cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(p.page_id)}
                            onChange={() => toggle(p.page_id)}
                            disabled={busy}
                          />
                          <span>{p.name || p.page_id}</span>
                        </label>
                      ))}
                      <label style={{ display: 'block', fontSize: 13, color: 'var(--sec)', margin: '12px 0 4px' }}>
                        Cliente que recebe os leads
                        <select
                          className="input"
                          style={{ display: 'block', width: '100%', marginTop: 4 }}
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          disabled={busy}
                        >
                          {clients.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        className="btn btn-black"
                        onClick={handleLink}
                        disabled={busy || !clientId || selected.length === 0}
                      >
                        {busy ? 'Vinculando…' : 'Vincular Páginas selecionadas'}
                      </button>
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
